// The Phaser side of encounter lighting (docs/19). One instance per scene; `setBoard` builds the
// light map for a board at an hour, `update` flickers it and follows the units.
//
// Draw order inside the board camera (bottom → top):
//   terrain            tiles and their props (torches, trees)
//   light              light map, MULTIPLY (ambient, shadows, every light) + halo and sun shafts, ADD
//   props              gameplay marks (exits, spawns, hazards) — above the light, never darkened
//   shadows            unit contact shadows, cast away from the strongest light
//   low                mist (below the units so it never hides a body)
//   units              tinted by the local light, but never below UNIT_FLOOR
//   high               embers, fireflies, motes, leaves, drips, and the seasons' petals, pollen,
//                      ice glints and breath (docs/23) (tiny, translucent)
// Weather (docs/20) is built with the light: its ground shader goes at the bottom of the light layer
// (under the light map), lightning at the top of it, splashes in low, rain and snow in high.
// Liquids (docs/28) too: their surface shader goes under the weather's ground (fog lies over the
// water), their spits, steam, fish and floating leaves in low.
// Plus a camera grade (ColorMatrix saturation/warmth + Vignette) on the board camera only; the
// overlay camera (intent glyphs, numbers, pings) is never touched by any of it.
import Phaser from 'phaser';
import { LightMap, crawlLights, findLights, lantern, type ArtLights, type Light, type MapRegion } from './lightmap';
import type { PaintedBoard } from '../encounter/types';
import type { Liquid } from '../liquid/liquid';
import { UNIT_FLOOR, climateFor, dayLight, luma, type Climate, type DayLight } from './daylight';
import type { TileData } from '../../core/sim/types';
import type { TiledBoard } from '../tiling';
import { fieldFade } from '../board/field';
import { faceOf } from '../hugeArt';
import { DataTexture } from '../board/dataTexture';
import { ViewZone, type Rect } from '../board/viewZone';
import { VIEW_MARGIN, viewRect } from '../board/viewRender';
import { addGrade, type Grade } from './gradeFilter';
import { WeatherLayer, type WeatherInfo } from '../weather/WeatherLayer';
import { weathered, type Weather } from '../weather/weather';
import { seasonHour, seasoned, type Season } from '../weather/season';
import { LiquidLayer, type LiquidInfo } from '../liquid/LiquidLayer';
import { kitFor } from '../tiling';

export type LightingMode = 'full' | 'soft' | 'off';

export interface LitUnit {
  key: string;
  /** Container in board pixels (pivot: bottom centre of the occupied cell). */
  obj: Phaser.GameObjects.Container;
  /** The image to tint (MULTIPLY tint mode only — flashes use FILL and are left alone). */
  body: Phaser.GameObjects.Image;
  hero: boolean;
  down: boolean;
  /** Board px its figure stands above the cell's floor (a kit hero's lift, docs/145): its shadow falls there. */
  stand?: () => number;
}

export interface LightingLayers { light: Phaser.GameObjects.Layer; shadows: Phaser.GameObjects.Layer; low: Phaser.GameObjects.Layer; high: Phaser.GameObjects.Layer }

export interface LightingInfo { hour: number; phase: string; setting: string; kit: string; lights: number; minLuma: number; mode: LightingMode; season?: Season; particles: string[]; weather?: WeatherInfo; liquid?: LiquidInfo }

let serial = 0;

/** Small generated textures the effects share (made once per texture manager). */
function ensureTextures(scene: Phaser.Scene): void {
  const tm = scene.textures;
  if (!tm.exists('fk-lt-dot')) {
    const c = tm.createCanvas('fk-lt-dot', 1, 1)!; const x = c.getContext(); x.fillStyle = '#fff'; x.fillRect(0, 0, 1, 1); c.refresh();
  }
  if (!tm.exists('fk-lt-leaf')) {
    const c = tm.createCanvas('fk-lt-leaf', 3, 2)!; const x = c.getContext(); x.fillStyle = '#fff'; x.fillRect(0, 0, 2, 1); x.fillRect(1, 1, 2, 1); c.refresh();
  }
  if (!tm.exists('fk-lt-petal')) {
    // a blossom petal: a pale 2×2 with one corner nicked and a brighter tip
    const c = tm.createCanvas('fk-lt-petal', 3, 2)!; const x = c.getContext();
    x.fillStyle = 'rgba(255,255,255,0.85)'; x.fillRect(0, 0, 2, 1); x.fillRect(1, 1, 2, 1); x.fillStyle = '#fff'; x.fillRect(1, 0, 1, 1); c.refresh();
  }
  if (!tm.exists('fk-lt-down')) {
    // thistledown: a 3×3 asterisk, faint arms round a bright seed
    const c = tm.createCanvas('fk-lt-down', 3, 3)!; const x = c.getContext();
    x.fillStyle = 'rgba(255,255,255,0.4)'; x.fillRect(0, 0, 1, 1); x.fillRect(2, 0, 1, 1); x.fillRect(0, 2, 1, 1); x.fillRect(2, 2, 1, 1); x.fillRect(1, 0, 1, 3); x.fillRect(0, 1, 3, 1);
    x.fillStyle = '#fff'; x.fillRect(1, 1, 1, 1); c.refresh();
  }
  if (!tm.exists('fk-lt-soft')) {
    const n = 32, c = tm.createCanvas('fk-lt-soft', n, n)!; const x = c.getContext();
    const g = x.createRadialGradient(n / 2, n / 2, 0, n / 2, n / 2, n / 2);
    g.addColorStop(0, 'rgba(255,255,255,1)'); g.addColorStop(0.5, 'rgba(255,255,255,0.45)'); g.addColorStop(1, 'rgba(255,255,255,0)');
    x.fillStyle = g; x.fillRect(0, 0, n, n); c.refresh(); c.setFilter(Phaser.Textures.FilterMode.LINEAR);
  }
}

const hex = (c: readonly [number, number, number]): number => (Math.round(Math.min(1, c[0]) * 255) << 16) | (Math.round(Math.min(1, c[1]) * 255) << 8) | Math.round(Math.min(1, c[2]) * 255);

export class LightingLayer {
  private map?: LightMap;
  private day?: DayLight;
  private climate?: Climate;
  private kit = '';
  private statics: Light[] = [];
  private keys?: { mul: string; glow: string };
  private tex?: { mul: DataTexture; glow: DataTexture };
  private images: Phaser.GameObjects.GameObject[] = [];
  private emitters: Phaser.GameObjects.Particles.ParticleEmitter[] = [];
  private zones: ViewZone[] = [];
  private shadowG?: Phaser.GameObjects.Graphics;
  private grade?: Grade;
  private units: LitUnit[] = [];
  private lastCompose = -1;
  /** The light map's samples composed last (the rest hold an earlier compose). */
  private fresh?: MapRegion;
  private mode: LightingMode = 'full';
  private still = false;
  private board?: { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> };
  private tiled?: TiledBoard;
  private weather: Weather = { kind: 'clear', intensity: 0, wind: 0 };
  private sky: WeatherLayer;
  private liquid: LiquidLayer;
  private flashes = true;
  private season?: Season;
  private particleKinds: string[] = [];
  private breath?: { emitter: Phaser.GameObjects.Particles.ParticleEmitter; next: Map<string, number> };

  constructor(private readonly scene: Phaser.Scene, private readonly layers: LightingLayers, private readonly cam: Phaser.Cameras.Scene2D.Camera, private readonly cell: number) {
    ensureTextures(scene);
    this.sky = new WeatherLayer(scene, layers, cell);
    this.liquid = new LiquidLayer(scene, layers.low, layers.light, cell);
    scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.destroy());
  }

  /**
   * Light a board at an hour, in a weather (docs/20) and a season (docs/23: the season remaps the
   * hour onto the sky for its day length, grades the light and dresses the air). Rebuilds
   * everything; cheap enough to call on every snapshot.
   */
  setBoard(board: { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> }, tiled: TiledBoard | undefined, kitKey: string, hour: number, weather?: Weather, season?: Season): void {
    this.board = board; this.tiled = tiled; this.kit = kitKey;
    this.climate = climateFor(kitKey);
    this.weather = weather ?? { kind: 'clear', intensity: 0, wind: 0 };
    this.season = season;
    const h = ((hour % 24) + 24) % 24;
    const day = weathered(seasoned(dayLight(seasonHour(h, season), this.climate), season, this.climate), this.weather);
    // the sky is read at the season's hour; the clock still says the room's hour
    this.day = { ...day, hour: h };
    this.rebuild();
  }

  /**
   * docs/186 EA5: the board is drawn in the commissioned art: its lights and liquids come from the painted
   * board (not the Oryx tiling) from the next `setBoard`; undefined goes back to the Oryx look's.
   */
  setArt(painted: Pick<PaintedBoard, 'cell' | 'lights' | 'liquids' | 'w'> | undefined): void {
    if (!painted) { this.art = undefined; return; }
    const c = painted.cell, w = painted.w;
    // the painter's acid shares the bog's slow bubbling surface (the shader knows four liquids)
    const liquids = painted.liquids.map((l): Liquid | undefined => (l === 'water' || l === 'deep' || l === 'lava' || l === 'mud' ? l : l === 'acid' ? 'mud' : undefined));
    this.art = { lights: { lights: painted.lights.map((l) => ({ x: l.x / c, y: l.y / c, kind: l.kind })), lava: (x, y) => liquids[y * w + x] === 'lava' }, liquids };
  }
  private art?: { lights: ArtLights; liquids: Array<Liquid | undefined> };
  private serialVersion = 0;
  /** Changes whenever the light map is rebuilt or dropped (EncounterTerrain re-tints its standing art). */
  get version(): number { return this.serialVersion; }
  /** The board is dark (night, underground): the art's lit pixels show over the light map. */
  get dark(): boolean { return !!this.map && !!this.day && this.mode !== 'off' && luma(this.day.ambient) < 0.75; }
  /** Flames on the board flicker (and motion is not reduced). */
  get flickering(): boolean { return !!this.map && !this.still && this.statics.some((l) => l.flicker === 'torch' || l.flicker === 'fire' || l.flicker === 'lava'); }

  /** docs/60 DC7: a crawl's lights, added to the board's own at the next `setBoard`. */
  setExtraLights(lights: ReadonlyArray<{ tile: { x: number; y: number }; radius: number; look: number }>): void { this.extra = lights; }
  private extra: ReadonlyArray<{ tile: { x: number; y: number }; radius: number; look: number }> = [];
  /** docs/63: squares behind a puzzle room's closed doors (row-major); their torches neither light nor smoke. */
  setUnseen(unseen: ReadonlyArray<boolean> | undefined): void { this.unseen = unseen; }
  private unseen: ReadonlyArray<boolean> | undefined;
  /**
   * docs/118 PT12: the field the air's particles and the weather may show over — the board, or
   * with squares unseen (a crawl, a puzzle room's closed doors) only what the party has seen, so
   * nothing drifting in the dark traces where the board goes on.
   */
  private seenField(): { w: number; h: number; tiles: ReadonlyArray<TileData | undefined> } {
    const board = this.board!, unseen = this.unseen;
    if (!unseen || unseen.length !== board.w * board.h) return board;
    return { w: board.w, h: board.h, tiles: board.tiles.map((t, i) => (unseen[i] ? ({ ...t, terrain: 'Void' } as TileData) : t)) };
  }

  /** `flashes: false` keeps the storm but never flashes lightning (photosensitivity, docs/20 W5). */
  setMode(mode: LightingMode, reduceMotion: boolean, flashes = true): void {
    if (mode === this.mode && reduceMotion === this.still && flashes === this.flashes) return;
    this.mode = mode; this.still = reduceMotion; this.flashes = flashes;
    if (this.board) this.rebuild();
  }

  setUnits(units: LitUnit[]): void {
    for (const u of this.units) if (u.body.active && u.body.tintMode === Phaser.TintModes.MULTIPLY) u.body.clearTint();
    this.units = units;
    this.lastCompose = -1;
  }

  info(): LightingInfo | undefined {
    if (!this.day || !this.map) return undefined;
    return { hour: this.day.hour, phase: this.day.phase, setting: this.day.setting, kit: this.kit, lights: this.statics.length, minLuma: this.map.minLuma(), mode: this.mode, season: this.season, particles: [...this.particleKinds], weather: this.sky.info(), liquid: this.liquid.info() };
  }

  get dayLight(): DayLight | undefined { return this.day; }
  /** docs/35: the light at a board point (squares), for the creatures; undefined when lighting is off. */
  lightAt(cx: number, cy: number): readonly [number, number, number] | undefined {
    if (!this.map || this.mode === 'off') return undefined;
    return this.map.sample(cx, cy);
  }
  /** docs/35: the sun is out and casting shadows (fliers draw one under them). */
  get sunny(): boolean { const s = this.day?.sun; return !!s && !s.moon && s.strength > 0.05 && this.mode !== 'off'; }
  get weatherNow(): Weather { return this.weather; }
  /** Review hook: a lightning strike now, when the weather and the settings allow one. */
  strike(): void { this.sky.strike(); }
  /** Review hook: a fish leaps now, when there is open water and the sky allows one. */
  leap(): void { this.liquid.leapNow(); }
  /** docs/93 §5.6: no fish leaps in a pool a water foe is in. */
  quietPools(foes: ReadonlyArray<{ x: number; y: number }>, w: number): void { this.liquid.quietPools(foes, w); }

  // ---- build ------------------------------------------------------------------------------------

  private clear(): void {
    for (const o of this.images) o.destroy();
    for (const e of this.emitters) e.destroy();
    this.images = []; this.emitters = []; this.zones = []; this.particleKinds = []; this.breath = undefined;
    this.shadowG?.destroy(); this.shadowG = undefined;
    this.sky.clear();
    this.liquid.clear();
    if (this.tex) { this.tex.mul.destroy(); this.tex.glow.destroy(); }
    this.keys = undefined; this.tex = undefined; this.map = undefined;
    if (this.grade) { this.cam.filters.external.remove(this.grade, true); this.grade = undefined; }
    for (const u of this.units) if (u.body.active && u.body.tintMode === Phaser.TintModes.MULTIPLY) u.body.clearTint();
  }

  private rebuild(): void {
    this.clear();
    this.serialVersion++;
    const board = this.board, day = this.day;
    if (!board || !day || this.mode === 'off') return;
    const { scene, cell } = this;
    this.statics = [...findLights(board, this.art ? undefined : this.tiled, day, this.art?.lights), ...crawlLights(board, this.extra, day)];
    if (this.unseen) {
      const unseen = this.unseen;
      this.statics = this.statics.filter((l) => !unseen[Math.floor(l.y) * board.w + Math.floor(l.x)]);
    }
    // sun shafts need the sun: a clear sky, or thin fog (where they read as god rays)
    const sunny = this.weather.kind === 'clear' || (this.weather.kind === 'fog' && this.weather.intensity < 0.7);
    const canopy = !!(this.climate?.atmosphere.motes || this.climate?.atmosphere.leaves) && !this.climate?.open && day.setting === 'outdoor' && this.mode === 'full' && sunny;
    this.map = new LightMap(board, day, { shafts: canopy });
    const id = ++serial;
    this.keys = { mul: `fk-lt-mul-${id}`, glow: `fk-lt-glow-${id}` };
    // the linear stretch is what makes a 4-sample-a-square grid read as smooth light
    this.tex = { mul: new DataTexture(scene, this.keys.mul, this.map.gw, this.map.gh, this.map.multiply, true), glow: new DataTexture(scene, this.keys.glow, this.map.gw, this.map.gh, this.map.glow, true) };
    const W = board.w * cell, H = board.h * cell;
    const mi = scene.add.image(0, 0, this.keys.mul).setOrigin(0, 0).setDisplaySize(W, H).setBlendMode(Phaser.BlendModes.MULTIPLY);
    const gi = scene.add.image(0, 0, this.keys.glow).setOrigin(0, 0).setDisplaySize(W, H).setBlendMode(Phaser.BlendModes.ADD);
    this.layers.light.add([mi, gi]);
    this.images.push(mi, gi);
    this.shadowG = scene.add.graphics();
    this.layers.shadows.add(this.shadowG);
    this.lastCompose = -1;
    this.compose(0);
    this.sky.build(this.seenField(), day, this.weather, { strength: this.strength(), particles: this.mode === 'full', still: this.still, flashes: this.flashes });
    this.liquid.build(board, kitFor(this.kit), this.art ? undefined : this.tiled, day, this.weather, this.season, { strength: this.strength(), particles: this.mode === 'full', still: this.still }, this.art?.liquids);
    if (this.mode === 'full') this.buildParticles();
    this.buildGrade();
  }

  private strength(): number { return this.mode === 'soft' ? 0.55 : 1; }

  /** Camera grade: the hour's saturation and warmth, a vignette that deepens with the dark (WebGL only). */
  private buildGrade(): void {
    const day = this.day!;
    if (this.scene.game.renderer.type !== Phaser.WEBGL) return;
    const k = this.strength();
    // one pass: the colour matrix, then the vignette over it (gradeFilter.ts)
    const cm = addGrade(this.cam, 0.5, 0.5, 0.92, day.vignette * k, 0x05070f);
    cm.colorMatrix.saturate(day.saturation * k, false);
    const w = day.warmth * k;
    cm.colorMatrix.multiply([1 + 0.07 * w, 0, 0, 0, 0, 0, 1 + 0.01 * w, 0, 0, 0, 0, 0, 1 - 0.09 * w, 0, 0, 0, 0, 0, 1, 0], true);
    // docs/23: the season's hue shift (eased toward identity in Soft), the heat's exposure, contrast
    if (day.matrix) cm.colorMatrix.multiply(day.matrix.map((v, i) => { const id = i % 6 === 0 ? 1 : 0; return id + (v - id) * k; }), true);
    if (day.exposure && day.exposure !== 1) cm.colorMatrix.brightness(1 + (day.exposure - 1) * k, true);
    if (day.contrast) {
      // ColorMatrix.contrast() puts its pivot offset in 0..1 but getData() divides offsets by 255, so
      // it only brightens: build the matrix with the offset in 0..255 (pivot at mid grey)
      const v = 1 + day.contrast * k, o = 0.5 * (1 - v) * 255;
      cm.colorMatrix.multiply([v, 0, 0, 0, o, 0, v, 0, 0, o, 0, 0, v, 0, o, 0, 0, 0, 1, 0], true);
    }
    this.grade = cm;
  }

  private buildParticles(): void {
    const day = this.day!, board = this.board!, cell = this.cell, scene = this.scene;
    const atm = day.atmosphere;
    const W = board.w * cell, H = board.h * cell;
    // mist and leaves go with the weather's wind (docs/20); a still day drifts gently east
    const wind = this.weather.kind === 'clear' && this.weather.intensity === 0 ? 0.25 : this.weather.wind;
    const dir = wind < 0 ? -1 : 1, gust = Math.abs(wind);
    const zone = (x: number, y: number, w: number, h: number): Phaser.Types.GameObjects.Particles.ParticleEmitterRandomZoneConfig & { rect: Rect } =>
      ({ type: 'random', rect: { x, y, w, h }, source: { getRandomPoint: (p: Phaser.Types.Math.Vector2Like) => { p.x = x + Math.random() * w; p.y = y + Math.random() * h; } } });
    // particles fade out towards the board's edge instead of drifting over the backdrop (the
    // field's own edge on a free-form board, docs/38 BS7)
    const fade = fieldFade(this.seenField(), cell);
    const inside = (p: Phaser.GameObjects.Particles.Particle, r: number): number => fade(p.x, p.y, r);
    // docs/118 PT12: with squares unseen, embers and drips keep to what was seen (a seen torch on a
    // wall never seen sends none up into the dark)
    const hide = !!this.unseen;
    const inside0 = (l: Light): number => fade(l.x * cell, l.y * cell, cell / 4);
    let kind = 'embers';
    const emit = (layer: Phaser.GameObjects.Layer, tex: string, cfg: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig): Phaser.GameObjects.Particles.ParticleEmitter => {
      // docs/147 PG4: air spawned over the whole board is spawned only where it can reach the view
      const rect = (cfg.emitZone as { rect?: Rect } | undefined)?.rect;
      const near = rect && !this.still ? ViewZone.wrap(this.cam, rect, cfg) : undefined;
      const e = scene.add.particles(0, 0, tex, near ? near.cfg : cfg);
      if (near) { near.zone.attach(e); this.zones.push(near.zone); }
      layer.add(e); this.emitters.push(e);
      if (!this.particleKinds.includes(kind)) this.particleKinds.push(kind);
      return e;
    };
    const area = board.w * board.h;
    // embers rise off every flame
    for (const l of this.statics) {
      if (l.flicker !== 'torch' && l.flicker !== 'fire') continue;
      emit(this.layers.high, 'fk-lt-dot', {
        x: l.x * cell, y: (l.y - (l.flicker === 'torch' ? 0.55 : 0.2)) * cell, lifespan: { min: 700, max: 1500 }, frequency: l.flicker === 'fire' ? 140 : 320, quantity: 1,
        speedX: { min: -4, max: 4 }, speedY: { min: -16, max: -7 }, alpha: hide ? { onEmit: () => inside0(l), onUpdate: (p, _k, t) => (1 - t) * inside(p, cell / 4) } : { start: 1, end: 0 }, tint: [0xffd27a, 0xff9a3c, 0xff6a2a],
        blendMode: Phaser.BlendModes.ADD, advance: 1500,
      });
    }
    if (atm.embers) {
      emit(this.layers.high, 'fk-lt-dot', { emitZone: zone(0, H * 0.2, W, H * 0.8), lifespan: { min: 2400, max: 4200 }, frequency: 160, speedX: { min: -3, max: 3 }, speedY: { min: -10, max: -4 }, alpha: hide ? { onEmit: () => 0, onUpdate: (p, _k, t) => 0.9 * (1 - t) * inside(p, cell / 2) } : { start: 0.9, end: 0 }, tint: [0xff9a3c, 0xff6a2a, 0xffd27a], blendMode: Phaser.BlendModes.ADD, advance: 4000 });
    }
    if (atm.fireflies) {
      kind = 'fireflies';
      // summer nights are thick with them (docs/23)
      const n = Math.round((area / 14) * (this.season === 'summer' ? 1.7 : 1));
      emit(this.layers.high, 'fk-lt-dot', {
        emitZone: zone(0, 0, W, H), lifespan: { min: 3500, max: 6500 }, frequency: 9000 / n, quantity: 1,
        speedX: { min: -5, max: 5 }, speedY: { min: -4, max: 4 }, tint: [0xd8ff7a, 0xf2ff9e, 0xb6ff6a], blendMode: Phaser.BlendModes.ADD,
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => Math.max(0, Math.sin(t * Math.PI)) * (0.55 + 0.45 * Math.sin(t * 40)) * inside(p, cell / 2) },
        scale: { min: 1, max: 2 }, advance: 6000,
      });
    }
    if (atm.motes) {
      kind = 'motes';
      // dust in the light: around flames and daylit doors indoors, across the board outdoors by day
      const pools = day.setting === 'indoor' ? this.statics.filter((l) => l.flicker === 'torch' || l.flicker === 'sky') : [];
      if (day.setting === 'outdoor') {
        emit(this.layers.high, 'fk-lt-dot', { emitZone: zone(0, 0, W, H), lifespan: { min: 4000, max: 7000 }, frequency: 260, speedX: { min: 1, max: 5 }, speedY: { min: -2, max: 2 }, alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.5 * Math.sin(t * Math.PI) * inside(p, cell / 2) }, tint: hex(day.sky), advance: 6000 });
      }
      for (const l of pools.slice(0, 12)) {
        const r = l.radius * 0.55 * cell;
        emit(this.layers.high, 'fk-lt-dot', { emitZone: zone(l.x * cell - r, l.y * cell - r * 0.6, r * 2, r * 1.6), lifespan: { min: 3000, max: 5500 }, frequency: 900, speedX: { min: -2, max: 2 }, speedY: { min: -3, max: 1 }, alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.45 * Math.sin(t * Math.PI) * inside(p, cell / 2) }, tint: hex(l.colour), blendMode: Phaser.BlendModes.ADD, advance: 4000 });
      }
    }
    if (atm.mist) {
      kind = 'mist';
      const tint = day.setting === 'indoor' ? hex([0.7, 0.75, 0.85]) : hex([0.82, 0.86, 0.95]);
      emit(this.layers.low, 'fk-lt-soft', {
        emitZone: zone(dir > 0 ? cell : cell * 3, cell, W - cell * 4, H - cell * 2), lifespan: { min: 9000, max: 14000 }, frequency: 1300 * (240 / Math.max(60, board.w * board.h)),
        speedX: { min: dir * (2 + 8 * gust), max: dir * (5 + 14 * gust) }, speedY: { min: -1, max: 1 }, scale: { min: 2.2, max: 4 }, tint,
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => (day.setting === 'indoor' ? 0.08 : 0.15) * Math.sin(t * Math.PI) * inside(p, cell * 2.5) }, advance: 12000,
      });
    }
    if (atm.leaves) {
      kind = 'leaves';
      emit(this.layers.high, 'fk-lt-leaf', { emitZone: zone(0, 0, W, H * 0.6), lifespan: { min: 4000, max: 6500 }, frequency: 700 / (0.6 + gust) / (this.season === 'autumn' ? 2.6 * Math.max(1, area / 216) : 1), speedX: { min: dir * (4 + 20 * gust), max: dir * (10 + 40 * gust) }, speedY: { min: 8, max: 16 }, rotate: { min: 0, max: 360 }, alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.9 * (1 - t) * inside(p, cell) }, tint: [0xd9822b, 0xe8a23a, 0xb5541f, 0xc9b23d], advance: 5000 });
    }
    if (atm.drips) {
      kind = 'drips';
      emit(this.layers.high, 'fk-lt-dot', { emitZone: zone(0, 0, W, H), lifespan: 520, frequency: 900, speedY: { min: 30, max: 40 }, scaleY: { start: 2, end: 1 }, alpha: hide ? { onEmit: () => 0, onUpdate: (p, _k, t) => 0.55 * (1 - t) * inside(p, cell / 2) } : { start: 0.55, end: 0 }, tint: 0xa8d8ff, advance: 1000 });
    }
    // ---- docs/23: the seasons' dressing and the heat's dust ----
    if (atm.petals) {
      // spring blossom: pale pink and white petals tumbling down the wind, fluttering side to side
      kind = 'petals';
      emit(this.layers.high, 'fk-lt-petal', {
        emitZone: zone(dir > 0 ? -cell : cell, 0, W, H * 0.7), lifespan: { min: 4500, max: 7500 }, frequency: 1100 / Math.max(1, area / 30) / (0.7 + gust),
        speedX: { min: dir * (5 + 16 * gust), max: dir * (12 + 34 * gust) }, speedY: { min: 5, max: 12 }, rotate: { min: 0, max: 360 },
        x: { onEmit: () => 0, onUpdate: (p, _k, t, v) => v + Math.sin(t * 14 + p.life * 0.011) * 0.35 },
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.9 * Math.min(1, t * 6, (1 - t) * 2.5) * inside(p, cell) },
        scale: { min: 1, max: 1.4 },
        tint: [0xffb3cf, 0xffc9dc, 0xffe0ea, 0xffffff, 0xf7c6ff, 0xff9fc2], advance: 6000,
      });
    }
    if (atm.pollen) {
      // summer: pollen motes drifting lazily in the sun, and now and then a seed of thistledown
      kind = 'pollen';
      emit(this.layers.high, 'fk-lt-dot', {
        emitZone: zone(0, 0, W, H), lifespan: { min: 5000, max: 8000 }, frequency: 3200 / Math.max(1, area / 20), speedX: { min: dir * 2, max: dir * (5 + 8 * gust) }, speedY: { min: -3, max: 2 },
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.75 * Math.sin(t * Math.PI) * inside(p, cell / 2) }, tint: [0xfff4b8, 0xffffff, 0xffe98a], advance: 7000,
      });
      emit(this.layers.high, 'fk-lt-down', {
        emitZone: zone(0, 0, W, H), lifespan: { min: 6000, max: 9000 }, frequency: 14000 / Math.max(1, area / 40), speedX: { min: dir * (4 + 10 * gust), max: dir * (8 + 18 * gust) }, speedY: { min: -4, max: 1 },
        y: { onEmit: () => 0, onUpdate: (p, _k, t, v) => v + Math.sin(t * 9 + p.life * 0.013) * 0.12 }, rotate: { min: 0, max: 45 },
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.7 * Math.sin(t * Math.PI) * inside(p, cell) }, tint: 0xffffff, advance: 8000,
      });
    }
    if (atm.glints) {
      // winter: diamond dust — single ice crystals hanging in the air, catching the light now and then
      kind = 'glints';
      const lum = Math.min(1, luma(day.ambient));
      emit(this.layers.high, 'fk-lt-dot', {
        emitZone: zone(0, 0, W, H), lifespan: { min: 3000, max: 5500 }, frequency: 3800 / Math.max(1, area / 25), speedX: { min: dir * 1, max: dir * (3 + 6 * gust) }, speedY: { min: 1, max: 4 },
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => (0.35 + 0.45 * Math.max(0, Math.sin(t * 23 + p.life))) * Math.sin(t * Math.PI) * (0.6 + 0.4 * lum) * inside(p, cell / 2) },
        tint: [0xffffff, 0xe6f2ff, 0xcfe6ff], blendMode: Phaser.BlendModes.ADD, advance: 5000,
      });
    }
    if (atm.rising) {
      // heat: dust and chaff lifting off the hot ground, slow and wavering
      kind = 'rising';
      emit(this.layers.high, 'fk-lt-dot', {
        emitZone: zone(0, H * 0.15, W, H * 0.85), lifespan: { min: 3500, max: 6000 }, frequency: 2600 / Math.max(1, area / 25), speedX: { min: -2, max: 2 }, speedY: { min: -9, max: -4 },
        x: { onEmit: () => 0, onUpdate: (p, _k, t, v) => v + Math.sin(t * 11 + p.life * 0.02) * 0.18 },
        alpha: { onEmit: () => 0, onUpdate: (p, _k, t) => 0.55 * Math.sin(t * Math.PI) * inside(p, cell / 2) }, tint: [0xfff0c8, 0xf4d9a0, 0xffffff], advance: 5000,
      });
    }
    if (day.breath && day.setting === 'outdoor') {
      // cold air: every so often each standing unit breathes out a faint puff (spawned in update())
      kind = 'breath';
      const e = emit(this.layers.high, 'fk-lt-soft', {
        emitting: false, lifespan: { min: 900, max: 1300 }, speedX: { min: -3, max: 3 }, speedY: { min: -6, max: -3 }, scale: { start: 0.12, end: 0.42 },
        alpha: { onEmit: () => 0, onUpdate: (_p, _k, t) => 0.32 * Math.sin(Math.min(1, t * 1.6) * Math.PI) * (1 - t) }, tint: 0xf2f7ff,
      });
      this.breath = { emitter: e, next: new Map() };
    }
    if (this.still) for (const e of this.emitters) e.stop();
  }

  /** Cold air (docs/23): each standing unit puffs a breath every ~2.5 s, on its own clock. */
  private breathe(time: number): void {
    const b = this.breath;
    if (!b || this.still) return;
    for (const u of this.units) {
      if (u.down || !u.obj.active || !u.obj.visible) continue;
      const due = b.next.get(u.key);
      if (due === undefined) { b.next.set(u.key, time + 400 + ((Number(u.key) * 977) % 2400)); continue; }
      if (time < due) continue;
      b.next.set(u.key, time + 2200 + Math.random() * 900);
      const face = faceOf(u.body);
      b.emitter.emitParticleAt(u.obj.x + face * this.cell * 0.18, u.obj.y - this.cell * 0.62, 1);
    }
  }

  // ---- per frame --------------------------------------------------------------------------------

  private dynamics(): Light[] {
    const day = this.day!;
    const out: Light[] = [];
    for (const u of this.units) {
      if (!u.hero || u.down || !u.obj.active) continue;
      const l = lantern(u.key, u.obj.x / this.cell, u.obj.y / this.cell - 0.5, day);
      if (l) out.push(l);
    }
    return out;
  }

  /**
   * The light map's samples the camera shows (`margin`: and that share of the view round them),
   * for composing only what is seen; undefined before the board is set.
   */
  private viewSamples(margin: number): MapRegion | undefined {
    const map = this.map, board = this.board;
    if (!map || !board) return undefined;
    const v = viewRect(this.cam, board.w * this.cell, board.h * this.cell, margin), k = map.S / this.cell;
    // one sample more each side: the linear stretch blends a texel with its neighbours
    return { i0: Math.floor(v.x * k) - 1, j0: Math.floor(v.y * k) - 1, i1: Math.ceil((v.x + v.w) * k) + 1, j1: Math.ceil((v.y + v.h) * k) + 1 };
  }

  /** `whole`: every sample (a new board, a still frame); otherwise the camera's view and a margin round it. */
  private compose(time: number, whole = true): void {
    const map = this.map, tex = this.tex;
    if (!map || !tex) return;
    const region = whole ? undefined : this.viewSamples(VIEW_MARGIN);
    map.compose([...this.statics, ...this.dynamics()], time / 1000, { strength: this.strength(), still: this.still }, region);
    this.fresh = region ?? { i0: 0, j0: 0, i1: map.gw, j1: map.gh };
    // only the samples composed go up to the GPU (docs/147 PG1)
    const part = { x0: this.fresh.i0, y0: this.fresh.j0, x1: this.fresh.i1, y1: this.fresh.j1 };
    tex.mul.upload(map.multiply, part);
    tex.glow.upload(map.glow, part);
  }

  /** The camera shows samples outside the part composed last: compose before this frame draws. */
  private offView(): boolean {
    const v = this.viewSamples(0), f = this.fresh;
    if (!v || !this.map) return false;
    const i0 = Math.max(0, v.i0), j0 = Math.max(0, v.j0), i1 = Math.min(this.map.gw, v.i1), j1 = Math.min(this.map.gh, v.j1);
    if (i1 <= i0 || j1 <= j0) return false;
    return !f || i0 < f.i0 || j0 < f.j0 || i1 > f.i1 || j1 > f.j1;
  }

  update(time: number): void {
    if (!this.map || !this.day) return;
    this.sky.update(time);
    this.liquid.update(time);
    for (const z of this.zones) z.update();
    this.breathe(time);
    // 30 Hz is plenty for a flame; still mode composes only when units move. Only what the camera
    // shows is composed — and at once, whatever the beat, when it looks past that
    const off = this.offView();
    if (!off && this.lastCompose >= 0 && time - this.lastCompose < 33) return;
    if (!off && this.still && this.lastCompose >= 0 && time - this.lastCompose < 250) return;
    this.lastCompose = time;
    this.compose(time, false);
    this.lightUnits();
  }

  /** Units take a little of the local light (warm by a torch, blue under the moon) and cast a soft shadow away from it. */
  private lightUnits(): void {
    const map = this.map!, day = this.day!, g = this.shadowG, cell = this.cell;
    g?.clear();
    const lights = this.statics.filter((l) => l.flicker !== 'sky' && l.flicker !== 'crystal' && l.flicker !== 'lava');
    for (const u of this.units) {
      if (!u.obj.active || !u.obj.visible) continue;
      const cx = u.obj.x / cell, cy = u.obj.y / cell - 0.5;
      const c = map.sample(cx, cy);
      const k = 0.3;   // share of the local light a unit takes
      const tint: [number, number, number] = [Math.max(UNIT_FLOOR, 1 - k * (1 - c[0])), Math.max(UNIT_FLOOR, 1 - k * (1 - c[1])), Math.max(UNIT_FLOOR, 1 - k * (1 - c[2]))];
      if (u.body.tintMode === Phaser.TintModes.MULTIPLY) u.body.setTint(hex(tint));
      if (!g || u.down) continue;
      // shadow direction: the sun's outdoors by day, otherwise away from the nearest flame
      let dx = 0, dy = 0, alpha = 0.22;
      if (day.sun && !day.sun.moon) { dx = day.sun.dx * Math.min(5, 2 + day.sun.length * 1.5); dy = day.sun.dy * Math.min(4, 1 + day.sun.length); alpha = 0.18 + day.sun.strength * 0.4; }
      else {
        let best: Light | undefined, bd = Infinity;
        for (const l of lights) { const d = Math.hypot(l.x - cx, l.y - (cy + 0.3)); if (d < l.radius && d < bd) { bd = d; best = l; } }
        if (best) { const d = Math.max(0.3, bd); dx = ((cx - best.x) / d) * 3; dy = ((cy + 0.3 - best.y) / d) * 2; alpha = 0.32; }
      }
      const fx = u.obj.x, fy = u.obj.y - 2 - (u.stand?.() ?? 0);
      g.fillStyle(0x05070f, alpha * u.obj.alpha);
      g.fillEllipse(fx + dx * 0.6, fy + dy * 0.6, cell * 0.62, cell * 0.22);
      g.fillStyle(0x05070f, alpha * 0.55 * u.obj.alpha);
      g.fillEllipse(fx + dx, fy + dy, cell * 0.5, cell * 0.18);
    }
  }

  destroy(): void {
    this.clear();
    this.units = [];
  }
}
